import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Navbar from "../components/Navbar";
import "./css/CommonComplete.css";
import uaeFlag from "../assets/icons/country_code.png"; // make sure path is correct

export default function ProfileComplete() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [authorizedPersonName, setAuthorizedPersonName] = useState("");
  const [authorizedPersonDesignation, setAuthorizedPersonDesignation] =
    useState("");
  const [companyStartDate, setCompanyStartDate] = useState("");

  const [touched, setTouched] = useState({});
  const [throwError, setThrowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const phoneRef = useRef(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      step: "Step",
      companyDetails: "Company Details",
      subtitle: "Please fill out the company details below.",
      companyName: "Company Name *",
      enterCompanyName: "Enter company name",
      invalidCompanyName: "Please enter a valid company name.",
      whatsappNumber: "WhatsApp Number (UAE)",
      whatsappPlaceholder: "50 XXX XXXX",
      invalidUaeMobile: "Invalid UAE mobile number.",
      invalidWhatsapp: "Please enter a valid WhatsApp number.",
      email: "Email *",
      enterEmail: "Enter email address",
      invalidEmail: "Please enter a valid email address.",
      authorizedPersonName: "Authorized Person Name *",
      enterName: "Enter name",
      invalidAuthorizedPersonName: "Please enter a valid authorized person name.",
      authorizedPersonDesignation: "Authorized Person Designation *",
      enterDesignation: "Enter designation",
      invalidAuthorizedPersonDesignation: "Please enter a valid authorized person designation.",
      companyStartDate: "Company Start Date *",
      invalidDate: "Please select a valid company start date.",
      continue: "Continue",
      failedToLoad: "Failed to load profile data",
      somethingWrong: "Something went wrong!",
    },
    ar: {
      loading: "جاري التحميل",
      step: "الخطوة",
      companyDetails: "تفاصيل الشركة",
      subtitle: "يرجى ملء تفاصيل الشركة أدناه.",
      companyName: "اسم الشركة *",
      enterCompanyName: "أدخل اسم الشركة",
      invalidCompanyName: "يرجى إدخال اسم شركة صحيح.",
      whatsappNumber: "رقم الواتساب (الإمارات)",
      whatsappPlaceholder: "50 XXX XXXX",
      invalidUaeMobile: "رقم جوال إماراتي غير صحيح.",
      invalidWhatsapp: "يرجى إدخال رقم واتساب صحيح.",
      email: "البريد الإلكتروني *",
      enterEmail: "أدخل عنوان البريد الإلكتروني",
      invalidEmail: "يرجى إدخال عنوان بريد إلكتروني صحيح.",
      authorizedPersonName: "اسم الشخص المخول *",
      enterName: "أدخل الاسم",
      invalidAuthorizedPersonName: "يرجى إدخال اسم شخص مخول صحيح.",
      authorizedPersonDesignation: "منصب الشخص المخول *",
      enterDesignation: "أدخل المنصب",
      invalidAuthorizedPersonDesignation: "يرجى إدخال منصب شخص مخول صحيح.",
      companyStartDate: "تاريخ بدء الشركة *",
      invalidDate: "يرجى اختيار تاريخ بدء شركة صحيح.",
      continue: "متابعة",
      failedToLoad: "فشل تحميل بيانات الملف الشخصي",
      somethingWrong: "حدث خطأ ما!",
    },
  }[lang];

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setInitialLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profile = data.response;
          
          // Set language preference from API response to localStorage
          if (profile.language_preference) {
            localStorage.setItem("app_language", profile.language_preference);
            setLang(profile.language_preference);
          }

          // Navigation logic based on profile_completed_step and is_verified
          const completedStep = profile.profile_completed_step || 0;
          
          // If onboarding is complete, navigate to dashboard
          if (completedStep === 5) {
            navigate("/dashboard");
            return;
          }
          
          // If verified, redirect from steps 1-3 to step 4
          const currentStep = 1;
          if (profile.is_verified === true && currentStep <= 3) {
            navigate("/profile-step-4");
            return;
          }
          
          // If current step is ahead of completed step + 1, redirect to appropriate step
          if (currentStep > completedStep + 1) {
            const nextStep = completedStep + 1;
            navigate(`/profile-step-${nextStep}`);
            return;
          }
          
          // Populate form fields if data is present
          if (profile.name) setName(profile.name);
          if (profile.email) setEmail(profile.email);
          if (profile.authorized_person_name) setAuthorizedPersonName(profile.authorized_person_name);
          if (profile.authorized_person_designation) setAuthorizedPersonDesignation(profile.authorized_person_designation);
          
          // Handle date - convert to YYYY-MM-DD format for date input
          if (profile.company_start_date) {
            const dateStr = profile.company_start_date;
            // If it's already in YYYY-MM-DD format, use it directly
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
              setCompanyStartDate(dateStr);
            } else {
              // If it's in ISO format or other format, extract YYYY-MM-DD
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setCompanyStartDate(`${year}-${month}-${day}`);
              }
            }
          }
          
          // Handle WhatsApp number - remove country code if present
          if (profile.whatsapp_number) {
            const phone = profile.whatsapp_number.toString();
            if (phone.startsWith("971")) {
              setWhatsapp(phone.substring(3));
            } else {
              setWhatsapp(phone);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        // Don't show error toast if profile doesn't exist (404) - user might be creating new profile
        if (err.code !== 404) {
          const currentLang = localStorage.getItem("app_language") || "en";
          const translations = {
            en: { failedToLoad: "Failed to load profile data" },
            ar: { failedToLoad: "فشل تحميل بيانات الملف الشخصي" },
          };
          showToast(err.message || translations[currentLang].failedToLoad, "error");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, [USER_BASE_URL, VERSION, navigate]);

  // ✅ Validators
  const validateName = (name) => /^[A-Za-z ]{1,100}$/.test(name.trim());
  const validateEmail = (email) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim()) &&
    email.trim().length >= 5 &&
    email.trim().length <= 255;
  const validateWhatsapp = (phone) => {
    if(whatsapp.trim() === "") return true;
    const cleaned = phone.trim();
    const startsValid = /^(50|52|54|55|56|58)/.test(cleaned);
    return /^[0-9]{9}$/.test(cleaned) && startsValid;
  };
  const validateText = (text) => /^[A-Za-z ]{1,100}$/.test(text.trim());
  const validateDate = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date);

  const validateInputs = () => {
    if (
      !validateName(name) ||
      !validateEmail(email) ||
      !validateText(authorizedPersonName) ||
      !validateText(authorizedPersonDesignation) ||
      !validateDate(companyStartDate)
    ) {
      setThrowError(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setThrowError(false);
    if (!validateInputs()) return;

    setLoading(true);
    try {
      const body = {
        name: name.trim(),
        email: email.trim(),
        authorized_person_name: authorizedPersonName.trim(),
        authorized_person_designation: authorizedPersonDesignation.trim(),
        company_start_date: companyStartDate,
      };

      if (whatsapp.trim() !== "") {
        body.whatsapp_number = "971" + whatsapp.trim();
      }

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/complete-profile/step-1`,
        "POST",
        JSON.stringify(body),
        { "Content-Type": "application/json" }
      );

      setLoading(false);
      if (data && data.response_code === 200) {
        if (data.response?.name) localStorage.setItem("username", data.response?.name);
        showToast(data.message, "success");
        setTimeout(() => navigate("/profile-step-2"), 500);
      } else {
        const currentLang = localStorage.getItem("app_language") || "en";
        const translations = {
          en: { somethingWrong: "Something went wrong!" },
          ar: { somethingWrong: "حدث خطأ ما!" },
        };
        showToast(data.message || translations[currentLang].somethingWrong, "error");
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      showToast(err.message, "error");
    }
  };

  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Navbar is_back_arrow={true} is_step_heading={true} heading={t.step} step_number={1} />

      <h2 className="title">{t.companyDetails}</h2>
      <p className="subtitle">{t.subtitle}</p>

      <form className="pc-form">
        {/* Company Name */}
        <p className="form-field-title">{t.companyName}</p>
        <div className="input-field-div">
          <input
            type="text"
            placeholder={t.enterCompanyName}
            maxLength={100}
            className="pc-input"
            value={name}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z\s]/g, "")
                .replace(/\s{2,}/g, " ");
              setName(value);
              setTouched((t) => ({ ...t, name: true }));
            }}
          />
          {(touched.name || throwError) && !validateName(name) && (
            <div className="error">{t.invalidCompanyName}</div>
          )}
        </div>

        {/* WhatsApp Number */}
        <p className="form-field-title">{t.whatsappNumber}</p>
        <div
          className={`input-container ${
            (touched.whatsapp || throwError) && !validateWhatsapp(whatsapp)
              ? "error"
              : ""
          }`}
        >
          <img className="country-code" src={uaeFlag} alt="UAE" />
          <input
            ref={phoneRef}
            type="text"
            maxLength={9}
            inputMode="numeric"
            placeholder={t.whatsappPlaceholder}
            value={whatsapp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              const isValidStart = /^(50|52|54|55|56|58)/.test(value);

              if (/^[0-9]*$/.test(value) && value.length <= 9) {
                setWhatsapp(value);
                if (value.length === 9) {
                  phoneRef.current?.blur();
                  if (!isValidStart) {
                    showToast(t.invalidUaeMobile, "error");
                    setWhatsapp("");
                  }
                }
              }
              setTouched((t) => ({ ...t, whatsapp: true }));
            }}
          />
        </div>
        {(touched.whatsapp || throwError) && !validateWhatsapp(whatsapp) && (
          <div className="error">{t.invalidWhatsapp}</div>
        )}

        {/* Email */}
        <p className="form-field-title">{t.email}</p>
        <div className="input-field-div">
          <input
            type="email"
            placeholder={t.enterEmail}
            maxLength={255}
            className="pc-input"
            value={email}
            onChange={(e) => {
              const value = e.target.value.replace(/[^a-zA-Z0-9@._+-]/g, "");
              setEmail(value);
              setTouched((t) => ({ ...t, email: true }));
            }}
          />
          {(touched.email || throwError) && !validateEmail(email) && (
            <div className="error">{t.invalidEmail}</div>
          )}
        </div>

        {/* Authorized Person Name */}
        <p className="form-field-title">{t.authorizedPersonName}</p>
        <div className="input-field-div">
          <input
            type="text"
            placeholder={t.enterName}
            maxLength={100}
            className="pc-input"
            value={authorizedPersonName}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z\s]/g, "")
                .replace(/\s{2,}/g, " ");
              setAuthorizedPersonName(value);
              setTouched((t) => ({ ...t, authorizedPersonName: true }));
            }}
          />
          {(touched.authorizedPersonName || throwError) &&
            !validateText(authorizedPersonName) && (
              <div className="error">
                {t.invalidAuthorizedPersonName}
              </div>
            )}
        </div>

        {/* Authorized Person Designation */}
        <p className="form-field-title">{t.authorizedPersonDesignation}</p>
        <div className="input-field-div">
          <input
            type="text"
            placeholder={t.enterDesignation}
            maxLength={100}
            className="pc-input"
            value={authorizedPersonDesignation}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z\s]/g, "")
                .replace(/\s{2,}/g, " ");
              setAuthorizedPersonDesignation(value);
              setTouched((t) => ({ ...t, authorizedPersonDesignation: true }));
            }}
          />
          {(touched.authorizedPersonDesignation || throwError) &&
            !validateText(authorizedPersonDesignation) && (
              <div className="error">
                {t.invalidAuthorizedPersonDesignation}
              </div>
            )}
        </div>

        {/* Company Start Date */}
        <p className="form-field-title">{t.companyStartDate}</p>
        <div className="input-field-div">
          <input
            type="date"
            className="pc-input"
            value={companyStartDate}
            max={new Date().toISOString().split("T")[0]} // disables future dates
            onChange={(e) => {
              setCompanyStartDate(e.target.value);
              setTouched((t) => ({ ...t, companyStartDate: true }));
            }}
          />
          {(touched.companyStartDate || throwError) &&
            !validateDate(companyStartDate) && (
              <div className="error">{t.invalidDate}</div>
            )}
        </div>
      </form>

      <button
        onClick={handleSubmit}
        type="submit"
        className="continue-btn"
        disabled={loading}
      >
        {loading ? <span className="loader"></span> : t.continue}
      </button>
    </div>
  );
}